// **Eine Lizenz anlegen oder ändern** — eine Seite (`/licenses/new`,
// `/licenses/<id>`), kein Dialog. Speichern schickt die geänderte Vorlage
// sofort an jeden laufenden Mandanten, der sie nutzt; danach wird der Katalog
// neu gelesen (die Formulare der Mandanten lesen die Vorlagen dort), statt ihn
// an Ort und Stelle zu verändern.

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, del, post, put } from "../../api/client";
import { keys, useLicenseTemplates } from "../../api/queries";
import type { LicenseTemplateUsage } from "../../api/types";
import { PageHead } from "../../app/page-head";
import { errorText } from "../../lib/labels";
import { Link, useNavigate } from "../../router/router";
import { ErrorNote, Loading } from "../../ui/badge";
import { Card } from "../../ui/card";
import { ConfirmButton } from "../../ui/confirm-button";
import { Check, Field, Input, Textarea } from "../../ui/field";
import { toast } from "../../ui/toaster";
import { LICENSE_ERRORS, NEW_RIGHTS, RIGHTS, fromTemplate, savedMessage, toBody, type LicenseFormState } from "./license-model";

const NEW_TEMPLATE = { key: "", name: "", description: "", price_eur_month: null, rights: NEW_RIGHTS, tenants: [] as LicenseTemplateUsage["tenants"] };

function failText(err: unknown): string {
  return (err instanceof ApiError ? LICENSE_ERRORS[err.code] : undefined) ?? errorText(err);
}

export function LicenseEditorView({ id }: { id: number | "new" }) {
  const navigate = useNavigate();
  const query = useLicenseTemplates();
  const tpl = id === "new" ? null : query.data?.find((x) => x.id === id);
  const missing = id !== "new" && query.isSuccess && !tpl;
  // Eine Vorlage, die es nicht (mehr) gibt: zurück zur Liste.
  useEffect(() => { if (missing) navigate("/licenses", { replace: true }); }, [missing, navigate]);

  if (id === "new") return <Editor isNew template={NEW_TEMPLATE} />;
  if (query.isError) return <><PageHead title="Lizenz" back={["/licenses", "Lizenzen"]} /><ErrorNote error={query.error} /></>;
  if (!tpl) return <Loading />;
  return <Editor isNew={false} template={tpl} templateId={tpl.id} />;
}

function Editor({ isNew, template, templateId }: {
  isNew: boolean;
  template: typeof NEW_TEMPLATE | LicenseTemplateUsage;
  templateId?: number;
}) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [form, setForm] = useState<LicenseFormState>(() => fromTemplate(template));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof LicenseFormState>(k: K, v: LicenseFormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const used = template.tenants;

  /**
   * Katalog und Liste neu lesen — beim nächsten Öffnen eines Formulars stimmt
   * die Vorlage. Der Katalog sofort (`refetchType: "all"`), auch wenn gerade
   * keine Ansicht ihn beobachtet: So wie früher, als die Liste nachgeladen wurde.
   */
  const refreshCatalog = () => Promise.all([
    client.invalidateQueries({ queryKey: keys.catalog, refetchType: "all" }),
    client.invalidateQueries({ queryKey: keys.licenseTemplates }),
  ]).catch(() => { /* beim nächsten Laden */ });

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await post("/license-templates", toBody(form, true));
        toast("Lizenz angelegt");
      } else {
        const res = await put<{ pushed: string[]; failed: string[] }>(`/license-templates/${templateId}`, toBody(form, false));
        const [msg, tone] = savedMessage(res);
        toast(msg, tone);
      }
      await refreshCatalog();
      navigate("/licenses", { replace: true });
    } catch (err) {
      setError(failText(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await del(`/license-templates/${templateId}`);
      await refreshCatalog();
      toast("Gelöscht");
      navigate("/licenses", { replace: true });
    } catch (err) { setError(failText(err)); }
  };

  return (
    <>
      <PageHead title={isNew ? "Neue Lizenz" : template.name} back={["/licenses", "Lizenzen"]} />
      <Card
        title="Lizenz"
        hint={isNew
          ? "Die Kennung steht später an jedem Benutzer in den Instanzen und lässt sich nach dem Anlegen nicht mehr ändern."
          : (
            <>
              Kennung <code>{template.key}</code>.{" "}
              {used.length
                ? <>Genutzt von {used.map((t, i) => <span key={t.id}>{i ? ", " : ""}<Link to={`/tenants/${t.id}/license`}>{t.name || t.slug}</Link> ({t.count})</span>)} — Speichern gilt dort sofort.</>
                : "Noch von keinem Mandanten genutzt."}
            </>
          )}
        foot={(
          <>
            <Link className="pf-btn" to="/licenses">Abbrechen</Link>
            {!isNew && (
              <ConfirmButton
                className="pf-btn pf-btn--danger" confirmLabel="Wirklich löschen?" onConfirm={remove}
                disabled={used.length > 0} title={used.length ? "Noch von Mandanten genutzt" : undefined}
              >Löschen</ConfirmButton>
            )}
            <button type="button" className="pf-btn pf-btn--primary" disabled={saving} onClick={() => void save()}>{isNew ? "Anlegen" : "Speichern"}</button>
          </>
        )}
      >
        <form className="pf-list" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <div className="pf-grid">
            <Field label="Name"><Input name="name" value={form.name} placeholder="z. B. Entwickler" onChange={(e) => set("name", e.target.value)} /></Field>
            {isNew && (
              <Field label="Kennung">
                <Input name="key" value={form.key} placeholder="z. B. entwickler" spellCheck={false} autoComplete="off" onChange={(e) => set("key", e.target.value)} />
              </Field>
            )}
            <Field label="Preis je Benutzer und Monat (EUR, netto)" hint="Leer = kein Preis hinterlegt.">
              <Input name="price" type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} />
            </Field>
            <Field label="Beschreibung (optional)" wide>
              <Textarea className="pf-input--prose" name="description" maxLength={500} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </Field>
          </div>
          <div className="pf-field"><span className="pf-field__label">Zugang</span>
            <div className="pf-checkgrid">
              {RIGHTS.map(([k, l, h]) => (
                <Check key={k} name={`r_${k}`} checked={form.flags[k]} label={l} hint={h} onChange={(on) => set("flags", { ...form.flags, [k]: on })} />
              ))}
            </div>
          </div>
          <div className="pf-grid">
            <Field label="Parallele Unterhaltungen" hint="Wie viele Turns gleichzeitig für einen Benutzer laufen (Chat, Agents, Code zusammen). Darüber wartet die Nachricht. Leer = unbegrenzt.">
              <Input name="max_parallel" type="number" min="1" step="1" value={form.max_parallel} onChange={(e) => set("max_parallel", e.target.value)} />
            </Field>
            <Field label="Routinen höchstens" hint="Leer = so viele, wie die Rolle erlaubt.">
              <Input name="max_routines" type="number" min="0" step="1" value={form.max_routines} onChange={(e) => set("max_routines", e.target.value)} />
            </Field>
          </div>
          <span className="pf-field__error" role="alert">{error}</span>
        </form>
      </Card>
    </>
  );
}
